<template>
    <BmvModal
        :value="value"
        size="md"
        :closable="!saving"
        :close-on-overlay="!saving"
        @close="close"
    >
        <template #header>
            <div class="pst-head" v-if="status">
                <span
                    class="pst-head-icon"
                    :style="status.color ? { background: status.color + '1f', color: status.color } : null"
                >
                    <i :class="status.icon || 'fal fa-tasks'"></i>
                </span>
                <div class="pst-head-body">
                    <h2 class="pst-head-title">{{ $t('post.status_transition.title') }}</h2>
                    <span class="pst-head-sub">
                        {{ $t('post.status_transition.subtitle', { status: status.name }) }}
                    </span>
                </div>
            </div>
        </template>

        <div class="pst-body" v-if="status">
            <div v-if="loadingReasons" class="pst-loading">
                <BmvLoader :active="true" />
            </div>

            <template v-else>
                <!-- Predefined reasons of the target status -->
                <div v-if="reasons.length" class="pst-field">
                    <label class="pst-label" for="pst-reason-select">
                        {{ $t('post.status_transition.reason') }}
                        <span v-if="reasonRequired" class="pst-required">*</span>
                    </label>
                    <select
                        id="pst-reason-select"
                        v-model="reasonId"
                        class="pst-input"
                        :disabled="saving"
                    >
                        <option :value="null">{{ $t('post.status_transition.reason_free_option') }}</option>
                        <option v-for="reason in reasons" :key="reason.id" :value="reason.id">
                            {{ label(reason) }}
                        </option>
                    </select>
                    <span v-if="fieldError('reason_id')" class="pst-error">{{ fieldError('reason_id') }}</span>
                </div>

                <!-- Free text: always available, it is the fallback when no
                     predefined reason fits. -->
                <div class="pst-field" v-if="!reasonId">
                    <label class="pst-label" for="pst-reason-text">
                        {{ reasons.length ? $t('post.status_transition.reason_free') : $t('post.status_transition.reason') }}
                        <span v-if="reasonRequired" class="pst-required">*</span>
                    </label>
                    <textarea
                        id="pst-reason-text"
                        v-model="reasonText"
                        class="pst-textarea"
                        rows="3"
                        :placeholder="$t('post.status_transition.reason_placeholder')"
                        :disabled="saving"
                    ></textarea>
                    <span v-if="localError" class="pst-error">{{ localError }}</span>
                    <span v-else-if="fieldError('reason')" class="pst-error">{{ fieldError('reason') }}</span>
                    <span v-else class="pst-hint">{{ $t('post.status_transition.reason_hint') }}</span>
                </div>

                <p v-if="hasReason" class="pst-notice">
                    <i class="fal fa-paper-plane"></i>
                    <span>{{ $t('post.status_transition.reason_sent') }}</span>
                </p>

                <!-- Estimated delivery: the follow-up given to a retained
                     contribution. Left untouched when the field is not edited. -->
                <div class="pst-field">
                    <label class="pst-label" for="pst-delivery">
                        {{ $t('post.status_transition.estimated_delivery') }}
                    </label>
                    <div class="pst-date-row">
                        <input
                            id="pst-delivery"
                            v-model="estimatedDelivery"
                            type="date"
                            class="pst-input"
                            :disabled="saving"
                        />
                        <button
                            v-if="estimatedDelivery"
                            type="button"
                            class="pst-clear"
                            :disabled="saving"
                            :aria-label="$t('post.status_transition.estimated_delivery_clear')"
                            @click="estimatedDelivery = ''"
                        >
                            <i class="fal fa-times"></i>
                        </button>
                    </div>
                    <span v-if="fieldError('estimated_delivery')" class="pst-error">
                        {{ fieldError('estimated_delivery') }}
                    </span>
                    <span v-else class="pst-hint">{{ $t('post.status_transition.estimated_delivery_hint') }}</span>
                </div>

                <p v-if="status.final" class="pst-notice pst-notice--warning">
                    <i class="fal fa-flag-checkered"></i>
                    <span>{{ $t('post.status_transition.final_notice') }}</span>
                </p>
            </template>
        </div>

        <template #footer>
            <BmvButton variant="ghost" size="md" :disabled="saving" @click="close">
                {{ $t('common.cancel') }}
            </BmvButton>
            <BmvButton
                variant="primary"
                size="md"
                icon="fal fa-check"
                :loading="saving"
                :disabled="loadingReasons"
                @click="submit"
            >
                {{ $t('post.status_transition.confirm') }}
            </BmvButton>
        </template>
    </BmvModal>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvModal from '@/components/UI/v2/BmvModal.vue'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import { activeReasons, reasonLabel } from '@/helpers/statusReasons'

export default {
    name: 'PostStatusTransition',
    components: { BmvModal, BmvButton, BmvLoader },
    props: {
        value: { type: Boolean, default: false },
        post: { type: Object, default: null },
        // Target status, as carried by the form (list of forms or form detail).
        status: { type: Object, default: null },
        formId: { type: Number, default: null }
    },
    data() {
        return {
            reasonId: null,
            reasonText: '',
            estimatedDelivery: '',
            reasons: [],
            loadingReasons: false,
            saving: false,
            localError: '',
            serverErrors: {}
        }
    },
    computed: {
        ...mapState('form', {
            cachedStatuses: (state) => state.formStatuses
        }),
        reasonRequired() {
            return !!(this.status && this.status.requires_reason)
        },
        hasReason() {
            return !!(this.reasonId || this.reasonText.trim())
        }
    },
    watch: {
        value(open) {
            if (open) this.init()
        }
    },
    methods: {
        ...mapActions('form', ['getFormStatuses']),
        ...mapActions('post', ['updateStatus']),
        label(reason) {
            return reasonLabel(reason, this.$i18n.locale, 'fr')
        },
        fieldError(field) {
            const messages = this.serverErrors[field]
            return messages ? [].concat(messages)[0] : ''
        },
        init() {
            this.reasonId = null
            this.reasonText = ''
            this.localError = ''
            this.serverErrors = {}
            // A date already set on the post is shown so the agent can confirm
            // or correct it; leaving it as is re-sends the same value.
            this.estimatedDelivery = (this.post && this.post.estimated_delivery)
                ? String(this.post.estimated_delivery).slice(0, 10)
                : ''
            this.loadReasons()
        },
        /**
         * Predefined reasons live on the statuses endpoint (the only one that
         * ships reasons.translations) — not on the form payload used to fill
         * the status dropdown. Statuses already fetched for this form are
         * reused from the store.
         */
        loadReasons() {
            this.reasons = []
            if (!this.status || !this.formId) return

            const cached = this.cachedStatuses && this.cachedStatuses[this.formId]
            if (cached) {
                this.reasons = activeReasons(this.findStatus(cached))
                return
            }

            this.loadingReasons = true
            this.getFormStatuses(this.formId)
                .then((statuses) => {
                    this.reasons = activeReasons(this.findStatus(statuses))
                })
                .catch(() => {
                    // Reasons are a typing convenience: free text still works,
                    // so a refused or failing call must not block the transition.
                    this.reasons = []
                })
                .finally(() => {
                    this.loadingReasons = false
                })
        },
        findStatus(statuses) {
            return (statuses || []).find((s) => s.id === this.status.id) || null
        },
        close() {
            if (this.saving) return
            this.$emit('input', false)
            this.$emit('cancel')
        },
        submit() {
            const freeText = this.reasonText.trim()

            if (this.reasonRequired && !this.reasonId && !freeText) {
                this.localError = this.$t('post.status_transition.reason_required')
                return
            }
            this.localError = ''
            this.serverErrors = {}

            // Only the keys the agent actually filled are sent: an absent key
            // leaves the stored value alone, an explicit null clears it.
            const payload = {
                id: this.post.id,
                form_status_id: this.status.id
            }
            if (this.reasonId) {
                payload.reason_id = this.reasonId
            } else if (freeText) {
                payload.reason = freeText
            }
            if (this.estimatedDelivery) {
                payload.estimated_delivery = this.estimatedDelivery
            } else if (this.post && this.post.estimated_delivery) {
                // Emptied on purpose: null clears the date server side.
                payload.estimated_delivery = null
            }

            this.saving = true
            this.updateStatus(payload)
                .then((response) => {
                    this.$emit('input', false)
                    this.$emit('done', { status: this.status, payload, response })
                })
                .catch((error) => {
                    const data = error && error.response && error.response.data
                    // 422 messages arrive already translated: they are shown as
                    // they come, next to the field they belong to.
                    if (error && error.response && error.response.status === 422 && data && data.errors) {
                        this.serverErrors = data.errors
                    } else {
                        this.$store.dispatch('dispatchError', error)
                    }
                })
                .finally(() => {
                    this.saving = false
                })
        }
    }
}
</script>

<style scoped>
.pst-head {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.pst-head-icon {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: rgba(8, 145, 178, 0.12);
    color: #0e7490;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
}

.pst-head-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.pst-head-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-text-1);
}

.pst-head-sub {
    font-size: 0.78rem;
    color: var(--v2-text-3);
    overflow-wrap: anywhere;
}

.pst-body {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
}

.pst-loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
}

.pst-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pst-label {
    font-size: 0.8rem;
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-2);
    margin: 0;
}

.pst-required {
    color: #dc2626;
}

.pst-input,
.pst-textarea {
    width: 100%;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-family: inherit;
    font-size: 0.85rem;
    color: var(--v2-text-1);
    background: var(--v2-surface-0);
    outline: none;
    box-sizing: border-box;
}

.pst-input {
    height: 34px;
}

.pst-textarea {
    padding: 8px 12px;
    line-height: 1.5;
    resize: vertical;
}

.pst-input:focus,
.pst-textarea:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.pst-date-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.pst-clear {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    cursor: pointer;
    padding: 0;
}

.pst-hint {
    font-size: var(--v2-text-xs-size);
    line-height: 1.45;
    color: var(--v2-text-3);
}

.pst-error {
    font-size: var(--v2-text-xs-size);
    line-height: 1.45;
    color: #dc2626;
}

.pst-notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    padding: 10px 12px;
    border-radius: var(--v2-radius-sm);
    background: rgba(8, 145, 178, 0.08);
    color: #0e7490;
    font-size: 0.78rem;
    line-height: 1.45;
}

.pst-notice--warning {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

@media (max-width: 640px) {
    .pst-head-title {
        font-size: 0.92rem;
    }
}
</style>
